import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSite, getSiteCollaborators, getSiteVercelProjects } from "@/lib/api";
import SiteEditForm from "@/pages/sites/siteEditForm";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { put } from "@rails/request.js";
import { useToast } from "@/hooks/use-toast";
import { DirectUpload } from "@rails/activestorage";

const SiteEditPage: React.FC = () => {
  const { siteSlug } = useParams<{ siteSlug: string }>();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch the individual site
  const { data: site, isLoading, isError, error } = useQuery({
    queryKey: ["site", siteSlug],
    queryFn: () => getSite(siteSlug!),
    enabled: !!siteSlug,
  });

  // Fetch collaborators for deployer_user_id select
  const { data: collaborators = [], isLoading: loadingCollaborators } = useQuery({
    queryKey: ["site-collaborators", site?.id],
    queryFn: () => getSiteCollaborators(site.slug),
    enabled: !!site?.slug,
  });

  // Fetch Vercel projects for vercel_project_id select
  const { data: vercelProjects = [], isLoading: loadingVercelProjects } = useQuery({
    queryKey: ["site-vercel-projects", site?.id],
    queryFn: () => getSiteVercelProjects(site.slug),
    enabled: !!site?.slug,
  });

  // State for CreateVercelProjectModal
  const [vercelModalOpen, setVercelModalOpen] = useState(false);

  // State for logo upload
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Form state
  const { register, handleSubmit, setValue, watch, formState, reset, control } = useForm({
    defaultValues: {
      name: "",
      subdomain: "",
      custom_domain: "",
      description: "",
      keywords: "",
      deployer_user_id: "",
      vercel_project_id: "",
      activated: false,
    },
  });

  // Sync form with site data when loaded
  useEffect(() => {
    if (site) {
      reset({
        name: site.name || "",
        subdomain: site.subdomain || "",
        custom_domain: site.custom_domain || "",
        description: site.description || "",
        keywords: site.keywords || "",
        deployer_user_id: site.deployer_user_id ? String(site.deployer_user_id) : "",
        vercel_project_id: site.vercel_project_id ? String(site.vercel_project_id) : "",
        activated: !!site.activated,
      });
    }
  }, [site, reset]);

  // Mutation for updating site
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      let logoSignedId = null;

      // If there's a logo file to upload, handle it first
      if (logoFile) {
        setUploadingLogo(true);
        try {
          logoSignedId = await new Promise<string>((resolve, reject) => {
            const upload = new DirectUpload(logoFile, "/rails/active_storage/direct_uploads");
            upload.create((error, blob) => {
              if (error) {
                reject(error);
              } else {
                resolve(blob.signed_id);
              }
            });
          });
        } catch (error: any) {
          throw new Error(`Failed to upload logo: ${error?.message || error}`);
        } finally {
          setUploadingLogo(false);
        }
      }

      const payload: any = {
        name: data.name,
        subdomain: data.subdomain,
        custom_domain: data.custom_domain,
        description: data.description,
        keywords: data.keywords,
        deployer_user_id: data.deployer_user_id,
        vercel_project_id: data.vercel_project_id,
        activated: data.activated,
      };

      // Add logo if uploaded
      if (logoSignedId) {
        payload.logo = logoSignedId;
      }

      const response = await put(`/sites/${siteSlug}.json`, {
        body: JSON.stringify({ site: payload }),
        responseKind: "json",
      });
      if (!response.ok) {
        const error = await response.json;
        throw new Error(error.errors ? error.errors.join(", ") : "Failed to update site");
      }
      return response.json;
    },
    onSuccess: () => {
      setLogoFile(null);
      queryClient.invalidateQueries({ queryKey: ["site", site?.slug] });
      toast({
        title: "Site updated",
        description: "The site was updated successfully.",
        variant: "success",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error?.message || "Failed to update site.",
        variant: "destructive",
      });
    },
  });

  // Watch all form values
  const values = watch();

  // Handler for field changes
  const handleFieldChange = (field: string, value: any) => {
    setValue(field as any, value, { shouldDirty: true });
  };

  // Handler for Vercel project creation
  const handleVercelProjectCreated = () => {
    if (site?.id) {
      queryClient.invalidateQueries({ queryKey: ["site-vercel-projects", site.id] });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-1/3 mb-4" />
        <Skeleton className="h-6 w-1/2 mb-2" />
        <Skeleton className="h-6 w-1/2 mb-2" />
        <Skeleton className="h-32 w-full mb-4" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          {error instanceof Error ? error.message : "Failed to load site data."}
        </Alert>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          Site not found.
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <h1 className="text-2xl font-bold mb-4">Edit Site</h1>
        </CardHeader>
        <CardContent>
          <SiteEditForm
            values={values}
            onChange={handleFieldChange}
            onSubmit={handleSubmit((data) => mutation.mutate(data))}
            isPending={mutation.isPending}
            isError={mutation.isError}
            errorMessage={mutation.isError ? (mutation.error as Error)?.message : undefined}
            collaborators={(collaborators || []).map((user: any) => ({
              id: String(user.id),
              email: user.email,
              name: user.name,
            }))}
            loadingCollaborators={loadingCollaborators}
            vercelProjects={(vercelProjects || []).map((project: any) => ({
              id: String(project.id),
              name: project.name,
            }))}
            loadingVercelProjects={loadingVercelProjects}
            vercelModalOpen={vercelModalOpen}
            setVercelModalOpen={setVercelModalOpen}
            onVercelProjectCreated={handleVercelProjectCreated}
            logoUrl={site?.logo_url}
            onLogoChange={setLogoFile}
            uploadingLogo={uploadingLogo}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteEditPage;
